/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * laws of Sweden.
 */

import React, { ReactElement, useEffect, useState } from "react";
import { AutoCompleteOptions } from "../../models/AutoCompleteOptions";
import { InputModeEnum } from "../../models/InputModeEnum";
import { strings } from "../../common/Strings/Strings";
import { MagnifyingGlass } from "../../common/Icons/MagnifyingGlass";
import { XMark } from "../../common/Icons/XMark";
import LoaderInline from "../LoaderInline";

/* eslint-disable react/no-unused-prop-types */
interface FieldProps {
  autoComplete?: AutoCompleteOptions;
  className?: string;
  clear?(): void;
  clinicId?: number;
  enableSearchButton?: boolean;
  filled?: boolean;
  inputMode?: InputModeEnum;
  label?: string;
  loading: boolean;
  onChange(e: string): void;
  onEnterDown?(): void;
  onSearch?(): void;
  placeholder?: string;
  readOnly?: boolean;
  toolTipText?: string | ReactElement;
  value?: string;
}

const SearchInputWithSearchButton = ({
  clear,
  autoComplete,
  enableSearchButton = true,
  inputMode,
  label,
  loading,
  onChange,
  onEnterDown,
  onSearch,
  placeholder = strings.search,
  readOnly,
  value: v,
}: FieldProps): ReactElement => {
  const [value, setValue] = useState<string>("");

  useEffect(() => {
    if (v) {
      setValue(v);
    }
  }, [v]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (onEnterDown && event.key === "Enter") {
      event.preventDefault();
      onEnterDown();
    }
  };

  return (
    <div>
      {label ? <label>{label}</label> : <></>}
      <div className="relative w-full">
        <input
          inputMode={inputMode}
          autoComplete={autoComplete || AutoCompleteOptions.off}
          className="tw-input pl-3 pr-28"
          name="search"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.value);
            setValue(e.target.value);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
          type="text"
          value={value}
        />
        {value && (
          <button
            className="absolute top-1/2 -translate-y-1/2 right-10 px-2 py-2 text-gray-500 hover:text-gray-700 hover:dark:text-gray-400 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            disabled={loading}
            onClick={() => {
              setValue("");
              onChange("");
              clear?.();
            }}
          >
            <XMark className="w-5 h-5" />
          </button>
        )}
        {enableSearchButton ? (
          <button
            className="absolute top-0 right-0 p-2.5 text-sm font-medium text-white bg-sky-500 rounded-r-lg border border-sky-500 hover:bg-sky-600 focus:ring-4 focus:outline-none focus:ring-sky-100 dark:bg-sky-500 dark:hover:bg-sky-600 dark:focus:ring-sky-800 transition-all duration-300"
            type="button"
            disabled={loading}
            onClick={onSearch}
          >
            {loading ? <LoaderInline /> : <MagnifyingGlass className="w-5 h-5 text-white stroke-2"/>}
          </button>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default SearchInputWithSearchButton;

SearchInputWithSearchButton.defaultProps = {
  autoComplete: AutoCompleteOptions.on,
  className: undefined,
  clinicId: undefined,
  inputMode: undefined,
  onEnterDown: undefined,
  onSearch: undefined,
  readOnly: false,
};